import { FC, useState } from 'react'
import {
    Avatar,
    MenuItem,
    IconButton,
    Tooltip,
    Menu,
    ListItemIcon,
    ListItemText,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Divider,
} from '@mui/material'
import AccountBoxIcon from '@mui/icons-material/AccountBox'
import HomeIcon from '@mui/icons-material/Home'
import LogoutIcon from '@mui/icons-material/Logout'
import NotificationsIcon from '@mui/icons-material/Notifications'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import LaunchIcon from '@mui/icons-material/Launch'
import { RoutePaths } from '@/navigation/route'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '@/context/useAuth'
import { formatInitials } from './formatInitials'
import { updateReleasesViews } from '@/services/user.service'

interface AvatarMenuProps {
    size?: number
    // Notification props
}

const AvatarMenu: FC<AvatarMenuProps> = ({ size }) => {

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [notificationDialogOpen, setNotificationDialogOpen] = useState(false)

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    const handleNotificationClick = () => {
        setNotificationDialogOpen(true)
        handleClose()
    }

    const handleNotificationDialogClose = async () => {
        setNotificationDialogOpen(false)
        await updateReleasesViews({ releaseId: me?.releases?.[0]?.releaseId })
        await fetchMe()
    }

    const handleOpenRelease = () => {
        handleNotificationDialogClose()
        if (me?.releases?.[0]?.redirectUrl) {
            window.open(me?.releases?.[0].redirectUrl, '_blank')
        }
    }

    const navigate = useNavigate()
    const { t } = useTranslation()
    const { me, handleSignOut, fetchMe } = useAuth()
    const isAgrid = me?.email?.includes('a-grid') ?? false

    if (!me) {
        return null
    }

    return (
        <>
            <Tooltip title="Account settings">
                <IconButton onClick={handleOpen}>
                    <Badge
                        color="error"
                        // variant="dot"
                        invisible={(me?.releases?.length == 0 && isAgrid) || !isAgrid}
                        // sx={{
                        //     '& .MuiBadge-badge': {
                        //         right: 0,
                        //         top: 0,
                        //     },
                        // }}
                        badgeContent={1}
                    >
                        <Avatar sx={{ width: size, height: size }}>{formatInitials(me)}</Avatar>
                    </Badge>
                </IconButton>
            </Tooltip>

            <Menu open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose}>
                {/* Notification MenuItem - only show if there's a notification */}
                {isAgrid && me?.releases?.length > 0 && (
                    <>
                        <MenuItem onClick={handleNotificationClick}>
                            <ListItemIcon>
                                <Badge color="error" variant="dot">
                                    <NotificationsIcon fontSize="small" color="primary" />
                                </Badge>
                            </ListItemIcon>
                            <ListItemText primary={t('auth.newFeature')} secondary={me?.releases?.[0]?.title} />
                        </MenuItem>
                        <Divider />
                    </>
                )}

                <MenuItem
                    onClick={() => {
                        navigate(RoutePaths.Settings)
                        handleClose()
                    }}
                >
                    <ListItemIcon>
                        <AccountBoxIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('auth.accountSettings')} />
                </MenuItem>

                <MenuItem
                    onClick={() => {
                        navigate('/')
                        handleClose()
                    }}
                >
                    <ListItemIcon>
                        <HomeIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('auth.mySites')} />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        navigate(RoutePaths.Tickets)
                        handleClose()
                    }}
                >
                    <ListItemIcon>
                        <FormatListBulletedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('auth.myTickets')} />
                </MenuItem>

                <MenuItem onClick={handleSignOut}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('auth.logout')} />
                </MenuItem>
            </Menu>

            {/* Notification Detail Dialog */}
            <Dialog open={notificationDialogOpen} onClose={handleNotificationDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsIcon color="primary" />
                    {t('auth.newFeature')}
                </DialogTitle>

                <DialogContent>
                    {me?.releases?.[0]?.path && (
                        <Box 
                            sx={{ 
                                mb: 3, 
                                display: 'flex', 
                                justifyContent: 'center',
                                borderRadius: 2,
                                overflow: 'hidden'
                            }}
                        >
                            <img 
                                src={me.releases[0].path} 
                                alt={me.releases[0].title}
                                style={{ 
                                    maxWidth: '100%', 
                                    maxHeight: 200,
                                    height: 'auto',
                                    borderRadius: '8px'
                                }}
                            />
                        </Box>
                    )}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            {me?.releases?.[0]?.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {me?.releases?.[0]?.description}
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleNotificationDialogClose}>{t('auth.later')}</Button>
                    <Button variant="contained" startIcon={<LaunchIcon />} onClick={handleOpenRelease}>
                        {t('auth.learnMore')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default AvatarMenu
